import { useContext, useEffect, useState } from "react";

import ParamsContext from "context/params/paramsContext";
import { API_IS_BEING_CANCELLED } from "shared/constants/strings";
import { useApi } from "shared/hooks/useApi";
import { useIsMounted } from "shared/hooks/useIsMounted";
import { usePermissions } from "shared/hooks/usePermissions";
import { useTct } from "shared/hooks/useTct";
import { ENDPOINTS, FILTER_NAMES } from "shared/types/enums";
import { Query } from "shared/types/interfaces";

import { STEPS } from "../types";

export const useTctWebDialog = (isOpen: boolean, close: () => void) => {
    const { queryParams } = useContext(ParamsContext);

    const { isReadFileNotAllowed, isDownloadFileNotAllowed } = usePermissions();
    const { cancelSource, isCanceled, getData, handleResponse } = useApi();

    const { createConfigurationOnline, editConfigurationOnline } =
        useTct(close);

    const isMounted = useIsMounted();

    const [isLoading, setIsLoading] = useState(false);
    const [isEditDisabled, setIsEditDisabled] = useState(true);
    const [step, setStep] = useState(STEPS.Initial);

    const isInitialStep = step === STEPS.Initial;
    const hasNoPermission = isReadFileNotAllowed || isDownloadFileNotAllowed;

    const [selectedConfiguration, setSelectedConfiguration] =
        useState<Query | null>(null);

    useEffect(() => {
        const getConfigurationCount = async () => {
            try {
                setIsLoading(true);

                const rootCompanyId = queryParams[FILTER_NAMES.RootCompany];

                const params = `?${FILTER_NAMES.RootCompany}=${rootCompanyId}&${FILTER_NAMES.WebEditorSupport}=true`;

                const {
                    data: { total }
                } = await getData(`${ENDPOINTS.Files}${params}`);

                if (isMounted) {
                    const isEmpty = total === 0;

                    setIsEditDisabled(isEmpty);
                }
            } catch (error) {
                if (isMounted && !isCanceled(error)) {
                    handleResponse(error);
                    setIsEditDisabled(true);
                }
            }

            setIsLoading(false);
        };

        !hasNoPermission && isOpen && getConfigurationCount();

        return () => {
            cancelSource.cancel(API_IS_BEING_CANCELLED);
        };

        // eslint-disable-next-line
    }, [isOpen, queryParams]);

    const handleStep = (value: STEPS) => setStep(value);

    const handleFileChange = (configurationValue: Query | null) =>
        setSelectedConfiguration(configurationValue);

    const handleSubmitCreate = () => createConfigurationOnline();

    const handleSubmitEdit = () => {
        selectedConfiguration &&
            editConfigurationOnline(selectedConfiguration?.id);
    };

    return {
        isLoading,
        hasNoPermission,
        isEditDisabled,
        isInitialStep,
        selectedConfiguration,
        setStep,
        setSelectedConfiguration,
        handleStep,
        handleFileChange,
        handleSubmitCreate,
        handleSubmitEdit
    };
};
